import * as THREE from 'three';
import type { Vec } from '../destruction/structure';
/** Small original geometry builder; all faces share a single vertex-color material. */
export class GeometryBuilder {
  positions: number[] = [];
  normals: number[] = [];
  colors: number[] = [];
  private sealed: { position: Float32Array; normal: Float32Array; color: Float32Array }[] = [];
  private sealedValues = 0;
  get vertexCount(): number {
    return (this.sealedValues + this.positions.length) / 3;
  }
  /** Stop growable JavaScript arrays before a single push must copy hundreds
   * of megabytes. Sealed ranges are immutable, already at GPU precision. */
  seal(): void {
    if (!this.positions.length) return;
    this.sealed.push({
      position: new Float32Array(this.positions),
      normal: new Float32Array(this.normals),
      color: new Float32Array(this.colors),
    });
    this.sealedValues += this.positions.length;
    this.positions = [];
    this.normals = [];
    this.colors = [];
  }
  sealIfLarge(): void {
    if (this.positions.length >= 49152) this.seal();
  }
  /** Concatenate prepared passes by sharing immutable ranges, without another
   * giant growable-array copy. The source remains usable for group counts. */
  append(other: GeometryBuilder): void {
    this.seal();
    other.seal();
    for (const segment of other.sealed) this.sealed.push(segment);
    this.sealedValues += other.sealedValues;
  }
  private palette = new Map<number, THREE.Color>();
  private color(hex: number): THREE.Color {
    let color = this.palette.get(hex);
    if (!color) this.palette.set(hex, (color = new THREE.Color(hex)));
    return color;
  }
  triangle(a: Vec, b: Vec, c: Vec, normal: Vec, color: number): void {
    const col = this.color(color);
    for (const p of [a, b, c]) {
      this.positions.push(p.x, p.y, p.z);
      this.normals.push(normal.x, normal.y, normal.z);
      this.colors.push(col.r, col.g, col.b);
    }
  }
  quad(a: Vec, b: Vec, c: Vec, d: Vec, n: Vec, color: number): void {
    const col = this.color(color);
    for (const p of [a, b, d, b, c, d]) {
      this.positions.push(p.x, p.y, p.z);
      this.normals.push(n.x, n.y, n.z);
      this.colors.push(col.r, col.g, col.b);
    }
  }
  box(
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    d: number,
    color: number,
    faces = [true, true, true, true, true, true],
  ): void {
    const X = x + w,
      Y = y + h,
      Z = z + d;
    const p = (a: number, b: number, c: number) => ({ x: a, y: b, z: c });
    if (faces[0]) this.quad(p(X, y, z), p(X, Y, z), p(X, Y, Z), p(X, y, Z), p(1, 0, 0), color);
    if (faces[1]) this.quad(p(x, y, Z), p(x, Y, Z), p(x, Y, z), p(x, y, z), p(-1, 0, 0), color);
    if (faces[2]) this.quad(p(x, Y, z), p(x, Y, Z), p(X, Y, Z), p(X, Y, z), p(0, 1, 0), color);
    if (faces[3]) this.quad(p(x, y, Z), p(x, y, z), p(X, y, z), p(X, y, Z), p(0, -1, 0), color);
    if (faces[4]) this.quad(p(X, y, Z), p(X, Y, Z), p(x, Y, Z), p(x, y, Z), p(0, 0, 1), color);
    if (faces[5]) this.quad(p(x, y, z), p(x, Y, z), p(X, Y, z), p(X, y, z), p(0, 0, -1), color);
  }
  geometry(): THREE.BufferGeometry {
    const steps = this.geometrySteps(Infinity);
    let result = steps.next();
    while (!result.done) result = steps.next();
    return result.value;
  }
  /** Conversion and bounds scans are part of preparation, not a single large
   * final commit. BufferAttribute keeps the prepared Float32 arrays directly. */
  *geometrySteps(chunk = 49152): Generator<void, THREE.BufferGeometry> {
    const g = new THREE.BufferGeometry();
    const stride = Math.max(3, Math.floor(chunk / 3) * 3);
    for (const [name, source] of [
      ['position', this.positions],
      ['normal', this.normals],
      ['color', this.colors],
    ] as const) {
      const target = new Float32Array(this.vertexCount * 3);
      let offset = 0;
      for (const range of [...this.sealed.map((segment) => segment[name]), source]) {
        for (let start = 0; start < range.length; start += stride) {
          const end = Math.min(start + stride, range.length);
          if (range instanceof Float32Array) target.set(range.subarray(start, end), offset + start);
          else for (let i = start; i < end; i++) target[offset + i] = range[i];
          yield;
        }
        offset += range.length;
      }
      g.setAttribute(name, new THREE.BufferAttribute(target, 3));
    }
    g.addGroup(0, this.vertexCount, 0);
    const positions = g.getAttribute('position').array;
    const box = new THREE.Box3(),
      point = new THREE.Vector3();
    for (let start = 0; start < positions.length; start += stride) {
      const end = Math.min(start + stride, positions.length);
      for (let i = start; i < end; i += 3)
        box.expandByPoint(point.set(positions[i], positions[i + 1], positions[i + 2]));
      yield;
    }
    const center = box.getCenter(new THREE.Vector3());
    let radiusSq = 0;
    for (let start = 0; start < positions.length; start += stride) {
      const end = Math.min(start + stride, positions.length);
      for (let i = start; i < end; i += 3) {
        const dx = positions[i] - center.x,
          dy = positions[i + 1] - center.y,
          dz = positions[i + 2] - center.z;
        radiusSq = Math.max(radiusSq, dx * dx + dy * dy + dz * dz);
      }
      yield;
    }
    g.boundingBox = box;
    g.boundingSphere = new THREE.Sphere(center, Math.sqrt(radiusSq));
    return g;
  }
}
